import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"

// Import the generated route tree
import { routeTree } from "../routeTree.gen"
import {
  AUTH_ME_QUERY_KEY,
  authMeQueryOptions,
  toRouterAuthState,
} from "./auth-query"
import { queryClient } from "./query-client"
import { pendingRouterAuthState } from "./router-context"

export const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: pendingRouterAuthState,
  },
})

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

export const AppRouterProvider = () => {
  const appQueryClient = useQueryClient()
  const meQuery = useQuery(authMeQueryOptions())
  const auth = toRouterAuthState(meQuery)

  const context = useMemo(() => {
    return {
      queryClient: appQueryClient,
      auth,
    }
  }, [appQueryClient, auth])

  useEffect(() => {
    void router.invalidate()
  }, [auth.status, auth.user?.userId, auth.user?.isAdmin])

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const controller = new AbortController()

    const refreshAuth = () => {
      void appQueryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshAuth()
      }
    }

    window.addEventListener("focus", refreshAuth, { signal: controller.signal })
    document.addEventListener("visibilitychange", onVisibilityChange, {
      signal: controller.signal,
    })

    return () => {
      controller.abort()
    }
  }, [appQueryClient])

  return <RouterProvider router={router} context={context} />
}
